import logger from '../../lib/log';
import { PaymentError } from '../../lib/error';
import {
  getBraintreeInstance,
  selectPaymentMethod,
  getDeviceData,
  updatePaymentInfo,
} from '../../lib/wallets';

// https://developers.braintreepayments.com/guides/venmo/client-side/javascript/v3
// https://github.com/braintree/popup-bridge-ios
// https://github.com/braintree/popup-bridge-android

export const PAYMENT_METHOD_ID = 'venmo';
export const PAYMENT_METHOD_NAME = 'Venmo';
export const ICON_FILENAME = 'payment-venmo-icon.svg';
export const AUTO_COMPLETE_PURCHASE = false;

const DEEP_LINK_RETURN_URL = 'com.fandango.popupbridge://popupbridgev1/venmo';

const log = logger('Venmo');

let authorized = false;
let venmoClientInstance;

/**
 * popupBridgeCompleteHandler
 *
 * The native apps uses popupbridge to open a window for authorizing venmo. This will be called
 * once the authorization in that new window is complete and the Braintree nonce will be included
 * in the payload. This will result in venmoClientInstance.tokenize() being resolved.
 *
 * OPEN QUESTION: Is the hash change used to pass along the nonce via the url without triggering a page refresh? What are the requirements?
 * or does Braintree also listen for this resonse and extract the nonce all on its own?
 *
 * @param {*} err
 * @param {Object} payload
 * @param {String} payload.hash - url fragment that will contain the braintree nonce -- e.g. `venmoSuccess=1&username=FANDANGOQA&paymentMethodNonce=fake-venmo-account-nonce`
 * @param {String} payload.path - expected to be `/venmo` to indicate that this is handling the callback from venmo
 * @param {Object} payload.queryItems - key value pairs
 */
const popupBridgeCompleteHandler = (err, payload) => {
  log('popupBridgeCompleteHandler', { err, payload });

  if (err) throw new PaymentError();

  if (_.get(payload, 'path') !== '/venmo') {
    log('popupBridgeCompleteHandler - Unrecognized url', payload);
    return;
  }

  // This is the proxied data triggered by the DEEP_LINK_RETURN_URL

  const queryString = _.map(
    payload.queryItems,
    (value, key) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
  ).join('&');
  const hash = queryString ? `${queryString}&${payload.hash}` : payload.hash;

  log('changing hash', hash);
  window.location.hash = hash;
};

/**
 * init
 *
 * Initializes Venmo as a payment method and uses Braintree instance to check for
 * browser support to ensure that it can be used as a payment method.
 *
 * @async
 * @param {Object} options
 * @param {Boolean} options.isAppWebView
 * @param {String} options.venmoProfileId
 *
 * @returns {Boolean} Flag to indicate that Venmo has been initialized
 *   successfully and can be used as a payment method.
 */
export const init = async ({ isAppWebView, venmoProfileId }) => {
  try {
    venmoClientInstance = await getBraintreeInstance({
      paymentMethodId: PAYMENT_METHOD_ID,
      config: {
        allowDesktop: true,
        allowNewBrowserTab: true,
        deepLinkReturnUrl: isAppWebView ? DEEP_LINK_RETURN_URL : undefined,
        profileId: venmoProfileId,
      },
    });
    const isBrowserSupported = venmoClientInstance.isBrowserSupported();
    log('isBrowserSupported', { isBrowserSupported });
    return isBrowserSupported;
  } catch (err) {
    log('DISABLED - error', err);
  }
  return false;
};

/**
 * select
 *
 * Saves Venmo as the selected payment method and prepares the Braintree
 * instance for use downstream during authorization. Additionally, if we
 * already have a tokenized result for venmo, we complete the authoriation
 * step as well.
 *
 * @async
 * @throws {PaymentError} Any errors thrown by `selectPaymentMethod` are simply propagated here
 */
export const select = async () => {
  await selectPaymentMethod({ paymentMethodId: PAYMENT_METHOD_ID });

  if (window.popupBridge) {
    window.popupBridge.onComplete = popupBridgeCompleteHandler;
  }

  // Check if tokenization results already exist. This occurs when your checkout page is relaunched in
  // a new tab. This step can be omitted if allowNewBrowserTab is false.
  if (!venmoClientInstance.hasTokenizationResult()) return;

  // eslint-disable-next-line no-use-before-define
  await authorize();
};

/**
 * authorize
 *
 * Authorizes Venmo as the payment method for the transaction and saves the Braintree nonce which is
 * used to complete the purchase.
 *
 * NOTE: If this is a webview - the venmo client instance will use the provided deeplink url to allow
 * popupbridge to pass the nonce from the new window back to the webview during the `tokenize` call.
 *
 * @async
 * @throws {PaymentError}
 */
export const authorize = async () => {
  try {
    const braintreeNoncePromise = venmoClientInstance.tokenize();
    const deviceDataPromise = getDeviceData();

    const deviceData = await deviceDataPromise;
    log('Device Data', deviceData);
    const response = await braintreeNoncePromise;
    log('Nonce', response);

    await updatePaymentInfo({
      walletId: PAYMENT_METHOD_ID,
      braintreeNonce: response.nonce,
      deviceData,
    });
    authorized = true;
  } catch (err) {
    log('Authorization error', err);
    authorized = false;
    throw new PaymentError(err);
  }
};

export const isAuthorized = () => authorized;

export default PAYMENT_METHOD_ID;
